/**
 * Data-driven permission families (`Data Security\{type}\{value}`). The values are data (rows in
 * a customer database), so only the family is declared; `.for(value)` resolves one leaf. The
 * path shape and the blank handling match the reporting service's `build_security_path`
 * (discovery E2) and `spApplyDataLevelSecurity`.
 *
 * A value must stay one segment directly below its family folder (red-team F-2): a value holding a
 * backslash would address a different node, so it is refused, as is a `.`/`..` value or one with a
 * character outside printable ASCII (`pathProblem`). Callers that resolve values read from a
 * database must expect `for()` to throw for such a row and handle it (it can never be granted
 * through Security Controls either, whose write routes apply the same shape rules).
 *
 * `for()` keeps the value's case, because `SecurityEntry` rows are case-preserved. Two spellings of
 * one value (`Cage`, `CAGE`) are the same row to SQL Server: compare permissions with
 * `samePermission()` / `permissionKey()`, never `===` (F-3).
 */
import { declareFamilyFolder, DATA_SECURITY_ROOT } from './builder';
import { MAX_PATH_LENGTH, PATH_SEPARATOR, pathProblem } from './path-rules';
import { registerFamily } from './registry';
import type { FamilyUniverse, PermissionFamily, SecurityPermission } from './types';

export const NOT_SELECTED = '(Not Selected)';

/** Module-private brand constructor for a validated family leaf. */
const asPermission = (path: string): SecurityPermission => path as SecurityPermission;

/** Why `value` cannot be a family leaf segment, or `null`. `value` is already trimmed. */
function valueProblem(value: string): string | null {
  if (value.includes(PATH_SEPARATOR)) return 'contains a backslash, so it would address a different node';
  return null;
}

export function family(securityType: string, config: { universe: FamilyUniverse }): PermissionFamily {
  declareFamilyFolder(securityType);
  const prefix = DATA_SECURITY_ROOT + PATH_SEPARATOR + securityType + PATH_SEPARATOR;

  const declared: PermissionFamily = {
    securityType,
    universe: { ...config.universe },
    for(value: string | null | undefined): SecurityPermission {
      if (value !== null && value !== undefined && typeof value !== 'string') {
        throw new Error(`Security registry: ${securityType} value must be a string.`);
      }
      const trimmed = (value ?? '').trim();
      const segment = trimmed.length === 0 ? NOT_SELECTED : trimmed;
      const problem = valueProblem(segment) ?? pathProblem(prefix + segment);
      if (problem) {
        throw new Error(`Security registry: ${securityType} value gives a path that ${problem} (max ${MAX_PATH_LENGTH}).`);
      }
      return asPermission(prefix + segment);
    },
  };
  registerFamily(declared);
  return declared;
}
